import { Component, OnInit } from '@angular/core';
import {
  ValidatorFn,
  ValidationErrors,
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl
} from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { Logger } from '@service/logger.service';
import { ResendService } from './resend.service';

import { ResendRequest, Agent, ContactChannel } from './resend.model';
import * as _ from 'lodash';
import { Document } from '../../core/enum/document.enum';
import { EncrDecrService } from '@service/encr-decr.service';

const log: Logger = new Logger('Resend');
const mobileNoFormat = /^[0]\d{9}$/;

@Component({
  selector: 'app-resend',
  templateUrl: './resend.component.html',
  styleUrls: ['./resend.component.scss']
})
export class ResendComponent implements OnInit {
  resendForm: UntypedFormGroup;

  loading = false;
  showResendForm = true;
  isResendError = false;
  isEditEmail = false;
  isEditMobile = false;

  validators: ValidatorFn[] = [this.validateEmail];
  errorMessages: object = {
    email: 'Invalid email format'
  };

  ePolicyId: number;
  userLogin: string;
  mails: string[];
  mobileNumber: string;
  mobileNumberWithoutEncode: string;
  docType: string;
  productType: string;

  public onResendClose: Subject<boolean> = new Subject();

  constructor(
    public resendModalRef: BsModalRef,
    private formBuilder: UntypedFormBuilder,
    private resendService: ResendService,
    private encrDecrService: EncrDecrService
  ) {}

  ngOnInit(): void {
    this.resendForm = this.getResendForm();
  }

  get emailTo(): AbstractControl {
    return this.resendForm.get('emailTo');
  }

  get sendSmsTo(): AbstractControl {
    return this.resendForm.get('sendSmsTo');
  }

  isDisableOkButton(): boolean {
    if (this.isRenewalNotice() || this.isTaxDeduct()) {
      return _.isEmpty(this.mobileNumber) || this.sendSmsTo.invalid || this.isEditEmail || this.isEditMobile;
    } else {
      return this.emailTo.invalid || this.sendSmsTo.invalid || this.isEditEmail || this.isEditMobile;
    }
  }

  isRenewalNotice(): boolean {
    return Document.RENEWAL_NOTICE === this.docType;
  }

  isMotorPolicy(): boolean {
    return Document.POLICY === this.docType && this.productType === 'MOTOR';
  }

  isTaxDeduct(): boolean {
    return Document.PA_TAXDEDUCT === this.docType;
  }

  validateEmail(control: AbstractControl): ValidationErrors {
    return Validators.email(control);
  }

  editMobile(): void {
    if (this.sendSmsTo.invalid) {
      return;
    } else if (this.sendSmsTo.value) {
      this.mobileNumber = this.sendSmsTo.value;
    }
    this.isEditMobile = !this.isEditMobile;
  }

  resend(): void {
    const agent: Agent = { userLogin: this.userLogin };

    const contactChannel: ContactChannel = {
      emailTo: this.emailTo.value,
      mobilePhoneNumber: this.encrDecrService.enCryption(this.getMobileNumber())
    };

    if (contactChannel.emailTo === null) {
      contactChannel.emailTo = [];
    }

    const resendRequest: ResendRequest = { agent, contactChannel };

    this.loading = true;
    this.resendService
      .resendPolicy(resendRequest, this.ePolicyId)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: () => {
          this.onResendClose.next(true);
          this.showResendForm = false;
        },
        error: (error: Error) => {
          this.isResendError = true;
          log.error(error);
        }
      });
  }

  closeModal(): void {
    this.resendModalRef.hide();
  }

  private getResendForm(): UntypedFormGroup {
    return this.formBuilder.group({
      emailTo: [this.mails, Validators.required],
      sendSmsTo: ['', Validators.pattern(mobileNoFormat)]
    });
  }

  private getMobileNumber(): string {
    let mobileNumber: string = this.mobileNumber;

    // if mobile number is default then decode to real data
    if (!mobileNoFormat.test(mobileNumber)) {
      mobileNumber = this.mobileNumberWithoutEncode;
    }

    return mobileNumber;
  }
}
